import { CheckCircle as CheckCircleIcon, HourglassIcon, TriangleAlert as TriangleAlertIcon } from "lucide-react";
import dayjs from "dayjs";
import type {
  AssessmentStageItem,
  AssessmentStages,
  BreachTrendChartData,
  BreachTrendItem,
  IssueDistribution,
  IssuesBySecurityChartData,
  RiskDistribution,
  TPRASummaryResponse,
  VendorRiskTrendItem,
  VendorsByRiskChartData,
  VendorsTrendChartData,
} from "../types/summary";

/**
 * Format month from API format (2024-06) to short format (Jun)
 * If data spans multiple years, include year suffix (Jun '24, Jan '25)
 */
export const formatMonth = (monthStr: string, allMonths?: string[]): string => {
  // If allMonths is provided, check if we have data spanning multiple years
  if (allMonths && allMonths.length > 0) {
    const years = new Set(allMonths.map((m) => dayjs(m).year()));
    if (years.size > 1) {
      // Multiple years - include year suffix
      return dayjs(monthStr).format("MMM 'YY");
    }
  }
  // Single year or no context - just show month
  return dayjs(monthStr).format("MMM");
};

/**
 * Transform risk distribution data for bar chart
 */
export const transformRiskDistribution = (riskDistribution: RiskDistribution): VendorsByRiskChartData[] => {
  return [
    { name: "High", value: riskDistribution.high, color: "#EC4899" },
    { name: "Medium", value: riskDistribution.medium, color: "#F59E0B" },
    { name: "Low", value: riskDistribution.low, color: "#10B981" },
    { name: "Pending", value: riskDistribution.pending, color: "#D1D5DB" },
  ];
};

/**
 * Transform issue distribution data for chart
 */
export const transformIssueDistribution = (issueDistribution: IssueDistribution): IssuesBySecurityChartData[] => {
  return [
    { name: "Critical", value: issueDistribution.critical, color: "#DC2626" },
    { name: "High", value: issueDistribution.high, color: "#EC4899" },
    { name: "Medium", value: issueDistribution.medium, color: "#F59E0B" },
    { name: "Low", value: issueDistribution.low, color: "#10B981" },
  ];
};

/**
 * Transform vendor risk trend data for line chart
 */
export const transformVendorRiskTrend = (vendorRiskTrend: VendorRiskTrendItem[]): VendorsTrendChartData[] => {
  // Get all month strings to detect year spanning
  const allMonths = vendorRiskTrend.map((item) => item.month);

  return vendorRiskTrend.map((item) => ({
    month: formatMonth(item.month, allMonths),
    All: item.all_vendors,
    High: item.high,
    Medium: item.medium,
    Low: item.low,
    Pending: item.pending ?? 0, // Default to 0 if backend doesn't provide
  }));
};

/**
 * Transform breach trend data for line chart
 */
export const transformBreachTrend = (breachTrend: BreachTrendItem[]): BreachTrendChartData[] => {
  // Get all month strings to detect year spanning
  const allMonths = breachTrend.map((item) => item.month);

  return breachTrend.map((item) => ({
    month: formatMonth(item.month, allMonths),
    breaches: item.breach_count,
  }));
};

/**
 * Transform assessment stages data for display cards
 */
export const transformAssessmentStages = (assessmentStages: AssessmentStages): AssessmentStageItem[] => {
  return [
    {
      label: "Intake Form",
      count: assessmentStages.intake_form,
      icon: TriangleAlertIcon,
      color: "#F59E0B",
      bgColor: "#FEF3C7",
    },
    {
      label: "Due Diligence",
      count: assessmentStages.due_diligence,
      icon: HourglassIcon,
      color: "#3B82F6",
      bgColor: "#DBEAFE",
    },
    {
      label: "Review",
      count: assessmentStages.review,
      icon: TriangleAlertIcon,
      color: "#F59E0B",
      bgColor: "#FEF3C7",
    },
    {
      label: "Reassessment",
      count: assessmentStages.reassessment,
      icon: HourglassIcon,
      color: "#3B82F6",
      bgColor: "#DBEAFE",
    },
    {
      label: "Completed",
      count: assessmentStages.complete,
      icon: CheckCircleIcon,
      color: "#10B981",
      bgColor: "#D1FAE5",
    },
  ];
};

/**
 * Transform complete TPRA summary response for UI consumption
 */
export const transformTPRASummary = (data: TPRASummaryResponse) => {
  return {
    metrics: [
      {
        label: "Sessions",
        value: data.total_vendors,
      },
      {
        label: "Third-Parties with Incidents",
        value: data.vendors_with_incidents, // Fixed field name
      },
      {
        label: "Critical and High Severity Issues",
        value: data.vendors_critical_business_impact,
      },
    ],
    issuesBySecurityData: transformIssueDistribution(data.issue_distribution), // New field
    vendorsByRiskData: transformRiskDistribution(data.risk_distribution),
    vendorsTrendData: transformVendorRiskTrend(data.vendor_risk_trend),
    breachTrendData: transformBreachTrend(data.breach_trend),
    assessmentStages: transformAssessmentStages(data.assessment_stages),
  };
};
